const asDate = (date: Date | string) => {
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString();
}

const asTime = (date: Date | string) => {
  const dateObj = new Date(date);

  // time will be formatted as 12-hour time with AM/PM without seconds

  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
}

/**
 * Display date as local (for wall-clock values from API sent without Z).
 * Use for course session dates so they match creation time.
 */
const asDateLocal = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleDateString();
};

/**
 * Display time as local (for wall-clock values from API sent without Z).
 * Use for course session times so they match creation time.
 */
const asTimeLocal = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const asDateWithTimezone = (date: Date | string | null | undefined, timezone?: string | null): string => {
  if (!date) return '';
  const dateObj = date instanceof Date ? date : new Date(date);

  if (timezone) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(dateObj);
  }

  return dateObj.toLocaleDateString();
}

const asTimeWithTimezone = (date: Date | string | null | undefined, timezone?: string | null): string => {
  if (!date) return '';
  const dateObj = date instanceof Date ? date : new Date(date);

  if (timezone) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(dateObj);
  }

  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
}

/**
 * Displays a time as "wall clock time" - the exact time stored without any timezone conversion.
 * Use this for course session times where "9 AM" should always display as "9 AM" regardless
 * of the viewer's timezone.
 * 
 * @param date - Date object or ISO string (assumes time is stored in UTC but represents wall clock time)
 * @returns Formatted time string (e.g., "9:00 AM")
 */
const asTimeUtc = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) return '';

  // Display the UTC time directly (no timezone conversion)
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(dateObj);
}

/**
 * Displays a date as "wall clock date" - the exact date stored without any timezone conversion.
 * Use this for course dates where "March 16" should always display as "March 16" regardless
 * of the viewer's timezone.
 * 
 * @param date - Date object or ISO string (assumes date is stored in UTC but represents wall clock date)
 * @returns Formatted date string (e.g., "03/16/2026")
 */
const asDateUtc = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) return '';

  // Display the UTC date directly (no timezone conversion)
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(dateObj);
}

const asDateTime = (date: Date | string) => {
  // date will be formatted as MM/DD/YYYY
  // time will be formatted as 12-hour time with AM/PM without seconds
  return `${asDate(date)} ${asTime(date)}`;
}

/**
 * Returns YYYY-MM-DD using UTC date components.
 * Use for "calendar date" values (e.g. dateOffered) that are stored as midnight UTC
 * so the chosen day is sent correctly regardless of entity timezone.
 */
const formatDateOnlyUtc = (date: Date | string | null | undefined): string | null => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Formats a date for use as startDateRange in API calls.
 * Returns date-only string (YYYY-MM-DD) without time or timezone.
 * Uses entity's localTimezone if provided to interpret the date correctly.
 */
const formatStartDateRange = (date: Date | string | null | undefined, timezone?: string | null): string | null => {
  if (!date) return null;
  return formatDateOnly(date, timezone);
}

/**
 * Formats a date for use as endDateRange in API calls.
 * Returns date-only string (YYYY-MM-DD) without time or timezone.
 * Uses entity's localTimezone if provided to interpret the date correctly.
 */
const formatEndDateRange = (date: Date | string | null | undefined, timezone?: string | null): string | null => {
  if (!date) return null;
  return formatDateOnly(date, timezone);
}

/**
 * Formats a date to YYYY-MM-DD string format (for date-only APIs).
 * Uses entity's localTimezone if provided to interpret the date correctly.
 * Always returns date without timezone information.
 */
const formatDateOnly = (date: Date | string | null | undefined, timezone?: string | null): string | null => {
  if (!date) return null;

  // If it's already a YYYY-MM-DD string, return it directly
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  // If it's a date string in YYYY-MM-DD format, parse it directly
  if (typeof date === 'string') {
    const dateMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
    }
  }

  // For Date objects or other formats, interpret in the entity's timezone if provided
  let dateObj: Date;
  if (typeof date === 'string') {
    // If we have a timezone, parse the date string in that timezone
    if (timezone) {
      // Parse the date string and create a date in the specified timezone
      const dateStr = date.split('T')[0]; // Get just the date part
      const [
        year, month, day,
      ] = dateStr.split('-').map(Number);
      if (year && month && day) {
        // Create date string in ISO format and use Intl to get local components
        const tempDate = new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00`);
        // Use Intl.DateTimeFormat to get date components in the specified timezone
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
        const parts = formatter.formatToParts(tempDate);
        const yearPart = parts.find(p => p.type === 'year')?.value;
        const monthPart = parts.find(p => p.type === 'month')?.value;
        const dayPart = parts.find(p => p.type === 'day')?.value;
        if (yearPart && monthPart && dayPart) {
          return `${yearPart}-${monthPart}-${dayPart}`;
        }
      }
    }
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }

  // If timezone is provided, format the date in that timezone
  if (timezone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = formatter.formatToParts(dateObj);
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    if (year && month && day) {
      return `${year}-${month}-${day}`;
    }
  }

  // Fallback to local date components (no timezone conversion)
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a datetime to YYYY-MM-DDTHH:mm:ss string format (for datetime APIs).
 * Uses entity's localTimezone if provided to interpret the datetime correctly.
 * Always returns datetime without timezone information.
 */
const formatDateTimeOnly = (date: Date | string | null | undefined, timezone?: string | null): string | null => {
  if (!date) return null;

  let dateObj: Date;
  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }

  // If timezone is provided, format the datetime in that timezone
  if (timezone) {
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const dateParts = dateFormatter.formatToParts(dateObj);
    const timeParts = timeFormatter.formatToParts(dateObj);
    const year = dateParts.find(p => p.type === 'year')?.value;
    const month = dateParts.find(p => p.type === 'month')?.value;
    const day = dateParts.find(p => p.type === 'day')?.value;
    const hour = timeParts.find(p => p.type === 'hour')?.value;
    const minute = timeParts.find(p => p.type === 'minute')?.value;
    const second = timeParts.find(p => p.type === 'second')?.value;
    if (year && month && day && hour && minute && second) {
      return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
    }
  }

  // Fallback to local datetime components (no timezone conversion)
  const year = String(dateObj.getFullYear());
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hour = String(dateObj.getHours()).padStart(2, '0');
  const minute = String(dateObj.getMinutes()).padStart(2, '0');
  const second = String(dateObj.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

/**
 * Converts a local datetime (in the specified timezone) to a UTC ISO string.
 * Use this when sending datetimes to the backend that the user entered in their local timezone.
 * 
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param timeStr - Time string in HH:mm format (24-hour)
 * @param timezone - The timezone the user entered the time in (e.g., "America/New_York")
 * @returns UTC ISO string (e.g., "2026-03-16T13:00:00.000Z") or null if invalid
 */
const localDateTimeToUtc = (
  dateStr: string | null | undefined,
  timeStr: string | null | undefined,
  timezone?: string | null
): string | null => {
  if (!dateStr || !timeStr) return null;

  // Parse the time components
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return null;

  // Create a reference date at the given time in UTC
  const refUtc = new Date(`${dateStr}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00Z`);
  if (isNaN(refUtc.getTime())) return null;

  if (!timezone) {
    // No timezone specified, return as-is (treat input as UTC)
    return refUtc.toISOString();
  }

  // Format the reference UTC date in the target timezone to find the offset
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(refUtc);
  const tzHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
  const tzMin = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
  const tzDay = parseInt(parts.find(p => p.type === 'day')?.value || '0', 10);

  // Get the reference day
  const refDay = refUtc.getUTCDate();

  // Calculate offset in minutes, accounting for day boundary crossings
  let offsetMinutes = (hours - tzHour) * 60 + (minutes - tzMin);
  
  // Adjust for day boundary (if timezone conversion crossed midnight)
  if (tzDay < refDay) {
    // Timezone is behind UTC and crossed to previous day
    offsetMinutes += 24 * 60;
  } else if (tzDay > refDay) {
    // Timezone is ahead of UTC and crossed to next day
    offsetMinutes -= 24 * 60;
  }

  // Apply the offset to convert from local time to UTC
  const utcDate = new Date(refUtc.getTime() + offsetMinutes * 60 * 1000);

  return utcDate.toISOString();
};

/**
 * Parse a session time from the API as UTC. API may return naive strings (UTC components without Z).
 * Use when displaying session times that are stored as UTC so they can be formatted in entity timezone.
 */
const parseSessionTimeAsUtc = (value: Date | string | null | undefined): Date | null => {
  if (value == null) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const str = String(value).trim();
  if (!str) return null;
  const utcStr = str.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(str) ? str : `${str}Z`;
  const d = new Date(utcStr);
  return isNaN(d.getTime()) ? null : d;
};

export const formatDate = {
  asDate,
  asTime,
  asDateLocal,
  asTimeLocal,
  asDateWithTimezone,
  asTimeWithTimezone,
  asDateUtc,
  asTimeUtc,
  asDateTime,
  formatStartDateRange,
  formatEndDateRange,
  formatDateOnly,
  formatDateOnlyUtc,
  formatDateTimeOnly,
  localDateTimeToUtc,
  parseSessionTimeAsUtc,
};